// ADR 8.9.26g §3 — what a button is, decided once.
//
// Thirty-three `<button>` elements shipped before this file and eleven of them
// carried no `className` at all, so the tutor's send control and every answer
// option in `QuestionCard` rendered as a raw browser default beside styled
// ones. That is not a framework problem — nothing had decided what a button
// *is*. This decides.
//
// Six variants, each a role (ADR 30.9.26c §2 added the fifth and sixth).
// `primary` is the page's one commitment; `tonal` a real action that is not
// that commitment (M3's filled tonal button: an accent-soft fill, no edge, so
// it never reads as the edged groups and option rows around it); `secondary`
// the outlined companion; `ghost` a control that is a control only while you
// are looking for it (a row action); `danger` the filled destructive action a
// page *urges*; and `danger-outlined` the destructive action a page *offers*
// (Android's error-coloured `OutlinedButton`). A treatment that is not one of
// these is a `className` — but a new role is a variant, because a caller's
// `border-danger` beside `secondary`'s `border-boundary` wins or loses on CSS
// key order, not on intent. A seventh has to name a role none of these is.
// ADR 16.10.26k §1 named it: `tab`, one control in a set where exactly one is
// shown — a selection, not an action, so none of the six describes it.
//
// `bg-accent` for `primary` is §1's one *visible* restyle: near-black
// (`bg-slate-900`) and indigo both shipped as primaries, and a token set cannot
// keep both without naming the near-black a second accent.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

export type ButtonVariant =
  | 'primary'
  | 'tonal'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outlined'
  | 'tab'

/**
 * The focus treatment, exported so `ui.test.tsx` can assert it is present
 * without pinning the utilities themselves (AC1).
 *
 * `focus-visible` rather than `focus`, so a pointer user does not get a ring
 * they did not ask for while a keyboard user still does. `outline` rather than
 * `ring`, because an outline follows the border radius without a second
 * offset colour to keep in sync with `--surface`.
 */
export const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'focus-visible:outline-focus'

/**
 * ADR 16.10.26k §1 — the attribute a caller already sets is what draws the
 * state. M3's selected toggle is the secondary container (`FilterChip`'s
 * `SELECTED`, the nav pill), not `accent-soft`, which is `tonal` at rest.
 * Exported, like `FOCUS_RING`, so the tests assert presence, not values.
 *
 * These win against plain variant utilities by specificity (0,2,0 against
 * 0,1,0) and against `hover:` / `active:` by Tailwind 3.4's emission order:
 * the `aria-*` variants register after the pseudo-classes. A Tailwind major
 * upgrade re-checks that by eye (AC9).
 */
export const PRESSED_STATE =
  'aria-pressed:bg-secondary-soft aria-pressed:text-on-secondary-soft ' +
  'aria-pressed:font-semibold aria-pressed:no-underline'

/** `aria-current` has no built-in modifier; each value is listed so `"false"` never matches. */
export const CURRENT_STATE =
  'aria-[current=true]:bg-secondary-soft aria-[current=true]:text-on-secondary-soft ' +
  'aria-[current=true]:font-semibold aria-[current=true]:no-underline ' +
  'aria-[current=page]:bg-secondary-soft aria-[current=page]:text-on-secondary-soft ' +
  'aria-[current=page]:font-semibold aria-[current=page]:no-underline ' +
  'aria-[current=step]:bg-secondary-soft aria-[current=step]:text-on-secondary-soft ' +
  'aria-[current=step]:font-semibold aria-[current=step]:no-underline'

// ADR 15.9.26af §5 — an 8px corner (M3 `shapes.small`), a 44px minimum (the
// smaller of Apple's and Material's targets — every variant, because a row
// action is the control a thumb misses most), and a colour transition that
// is `motion-safe:` so the intent is visible here and `styleFloor.test.ts`
// AC4 can see it. `duration-*` / `ease-*` need no prefix: without a
// `transition-property` they do nothing, and the reduced-motion rule zeroes
// the duration anyway.
const BASE =
  'inline-flex items-center justify-center gap-1 rounded-control min-h-11 text-label-lg ' +
  'motion-safe:transition-colors duration-short ease-standard ' +
  `${FOCUS_RING} disabled:opacity-50 ${PRESSED_STATE} ${CURRENT_STATE}`

// Hover and active fills are spelt with the alpha modifier §2 made possible:
// `bg-accent/90` emits `rgb(var(--accent) / 0.9)` now, where through a hex
// property it emitted nothing.
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent/90 active:bg-accent/80',
  // The hover is a lift, not a fade: an alpha pulls the fill toward the group
  // behind it, so the `/90` stays light and `shadow-1` carries the hover.
  // `BASE` transitions colours only, so the lift eases here.
  tonal:
    'bg-accent-soft px-3 py-1.5 font-medium text-on-accent-soft ' +
    'hover:bg-accent-soft/90 hover:shadow-1 active:bg-accent-soft/80 active:shadow-none ' +
    'motion-safe:transition-shadow',
  secondary:
    'border border-boundary px-3 py-1.5 text-ink hover:bg-surface-sunken active:bg-line/60',
  ghost: 'px-2 py-1 text-ink-muted underline hover:bg-surface-sunken active:bg-line/60',
  danger:
    'bg-danger px-3 py-1.5 font-medium text-on-danger hover:bg-danger/90 active:bg-danger/80',
  'danger-outlined':
    'border border-danger px-3 py-1.5 text-danger ' +
    'hover:bg-danger-soft hover:text-on-danger-soft active:bg-danger-soft/80',
  // ADR 16.10.26k §1 — no underline, so a tab stops reading as a link. The
  // selection is drawn three ways (rule, ink, weight), never colour alone;
  // `rounded-b-none` squares the corners against the tablist's rule.
  tab:
    'rounded-b-none border-b-2 border-transparent px-3 py-2 text-ink-muted ' +
    'hover:bg-surface-sunken hover:text-ink active:bg-line/60 ' +
    'aria-selected:border-accent aria-selected:text-accent aria-selected:font-semibold',
}

/**
 * ADR 17.9.26k §4 — the class string a variant composes, exported.
 *
 * For the element that has to *look* like a button and cannot *be* one: an
 * external `target="_blank"` link (an out-of-app CTA), where an `<a>`
 * around a `<Button>` would be a ring inside a ring and a button inside a
 * link. `Button` composes through this too, so there is one string and the
 * 44px floor, the corner and the ring 15.9.26af §5 put here stay here.
 */
export function buttonClassName(variant: ButtonVariant): string {
  return `${BASE} ${VARIANT[variant]}`
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  children?: ReactNode
}

/**
 * `type` defaults to `button` rather than to the platform's `submit`: every
 * one of the thirty-three call sites that had a type spelt `button`, and a
 * control inside a form that submits it by accident is the more expensive
 * mistake of the two. `RegisterPanel` and `ListsPage` pass `submit` explicitly.
 *
 * `disabled` is left as the native attribute and never simulated with a class:
 * that is what conveys the state to assistive technology, and dimming alone
 * would convey it by colour only (AC1).
 *
 * The ref reaches the `<button>` (ADR 16.10.26l §5): the builder's History
 * trigger takes focus back when its popover closes, as `IconButton`'s does.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', className = '', type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={`${buttonClassName(variant)}${className === '' ? '' : ` ${className}`}`}
      {...rest}
    />
  )
})
